// Vehicle sprites as data: rear views drawn in 4 shades ("." transparent, "#" darkest, "@" dark, "+" light, "o" lightest).
// The defaults below are the built-in Rally car, Bike and Kart; the admin editor can replace any of them (see TrackStore).
// No DOM or canvas here: the game, the server (validation) and the admin page all use this file.

/** A wheel's light tread marks are redrawn at a shifted row so they slide upwards: marks repeat every `p` rows, `thick` rows long. */
export interface Wheel { x: number; mw: number; y0: number; y1: number; m0: number; p: number; thick: number }
export const ROLL_PHASES = 6; // lcm of the periods 2 and 3
export const phases = Array.from({ length: ROLL_PHASES }, (_, i) => i);
export function applyRoll(rows: string[], wheels: Wheel[], phase: number): string[] {
  const g = rows.map((r) => [...r]);
  for (const w of wheels)
    for (let y = w.y0; y <= w.y1; y++) {
      if (!g[y]) continue;
      const on = ((((y - w.m0 + phase) % w.p) + w.p) % w.p) < w.thick;
      for (let x = w.x; x < w.x + w.mw; x++) if (g[y][x] === "#" || g[y][x] === "o") g[y][x] = on ? "o" : "#";
    }
  return g.map((r) => r.join(""));
}

export interface SpriteSpec {
  rows: string[]; // straight view
  wheels: Wheel[]; // rolling tyres of the straight view
  yaw: "auto" | "manual"; // how the turning poses are made
  lean: number; // auto: pixels the top row leans per level
  flank: [number, number, number]; // auto: [first row, last row, width per level] of the side panel that appears
  yaw1: string[]; // manual: pose turning right, level 1 (≈10°); the left pose is its mirror image
  yaw2: string[]; // manual: level 2 (≈20°)
  yawWheels: [Wheel[], Wheel[]]; // manual: tyres of each pose
  anchors: [number, number]; // manual: column of the car's centre line in each pose
  nominal: [number, number]; // width in pixels on screen at the player's row, types A and B
  front?: string[]; // front view, shown in the rear-view mirror (absent: the built-in front of that category)
}
export interface SpriteSet {
  w: number; // width of the straight sprite (columns)
  v: string[][]; // straight, one frame per wheel-roll phase
  yawR: string[][][]; // [level][phase], turning right
  yawL: string[][][]; // mirror images
  anchor: number[]; // centre column per yaw level
  nominal: [number, number];
  front: string[]; // seen from the front (rear-view mirror)
}
const mirrorRows = (rows: string[]) => rows.map((r) => [...r].reverse().join(""));

/** A yawed copy of a straight sprite: the upper rows lean towards the turn and a flank of the body shows on the turning side. */
export function yawFrom(rows: string[], level: number, lean: number, flank: [number, number, number]): string[] {
  const H = rows.length, W = rows[0].length;
  const extra = Math.ceil(lean * level) + flank[2] * level;
  return rows.map((r, y) => {
    const sh = H > 1 ? Math.round(((H - 1 - y) / (H - 1)) * lean * level) : 0;
    const row = Array(W + extra).fill(".");
    for (let x = 0; x < W; x++) if (r[x] !== ".") row[x + sh] = r[x];
    if (y >= flank[0] && y <= flank[1]) {
      let right = -1;
      for (let x = 0; x < W; x++) if (r[x] !== ".") right = x;
      const fw = flank[2] * level;
      for (let k = 1; k <= fw; k++) if (row[right + sh + k] === ".") row[right + sh + k] = k === fw || y === flank[0] || y === flank[1] ? "#" : "@";
    }
    return row.join("");
  });
}

/** The two turning poses (right-turning rows) a spec produces, before the wheel-roll marks. */
export function yawViews(s: SpriteSpec): { rows: [string[], string[]]; wheels: [Wheel[], Wheel[]]; anchor: [number, number] } {
  if (s.yaw === "manual") return { rows: [s.yaw1, s.yaw2], wheels: s.yawWheels, anchor: s.anchors };
  const c = (s.rows[0].length - 1) / 2;
  return { rows: [yawFrom(s.rows, 1, s.lean, s.flank), yawFrom(s.rows, 2, s.lean, s.flank)], wheels: [s.wheels, s.wheels], anchor: [c, c] };
}

export function buildSet(s: SpriteSpec, defaultFront: string[]): SpriteSet {
  const y = yawViews(s);
  const yawR = [[] as string[][], phases.map((ph) => applyRoll(y.rows[0], y.wheels[0], ph)), phases.map((ph) => applyRoll(y.rows[1], y.wheels[1], ph))];
  return {
    w: s.rows[0].length,
    v: phases.map((ph) => applyRoll(s.rows, s.wheels, ph)),
    yawR,
    yawL: yawR.map((l) => l.map(mirrorRows)),
    anchor: [y.anchor[0], y.anchor[0], y.anchor[1]],
    nominal: s.nominal,
    front: s.front ?? defaultFront,
  };
}

// ---- validation (server and admin page) ----
const SPRITE_CHARS = /^[.#@+o]+$/;
const isInt = (n: unknown): n is number => typeof n === "number" && Number.isInteger(n);
export const SPRITE_MAX_W = 48, SPRITE_MAX_H = 40;
function checkRows(rows: unknown, what: string, maxW = SPRITE_MAX_W): string | null {
  if (!Array.isArray(rows) || rows.length < 3 || rows.length > SPRITE_MAX_H) return `${what}: 3 to ${SPRITE_MAX_H} rows`;
  const w = typeof rows[0] === "string" ? rows[0].length : 0;
  if (w < 5 || w > maxW) return `${what}: width 5 to ${maxW}`;
  for (const r of rows) if (typeof r !== "string" || r.length !== w || !SPRITE_CHARS.test(r)) return `${what}: every row needs the same width and only . # @ + o`;
  if (!rows.some((r: string) => /[^.]/.test(r))) return `${what}: empty`;
  return null;
}
function checkWheels(ws: unknown, what: string, w: number, h: number): string | null {
  if (!Array.isArray(ws) || ws.length > 6) return `${what}: at most 6 wheels`;
  for (const k of ws as Wheel[]) {
    if (!k || ![k.x, k.mw, k.y0, k.y1, k.m0, k.p, k.thick].every(isInt)) return `${what}: wheel values must be integers`;
    if (k.x < 0 || k.mw < 1 || k.x + k.mw > w || k.y0 < 0 || k.y1 < k.y0 || k.y1 >= h) return `${what}: a wheel is outside the sprite`;
    if (k.p < 1 || k.p > 6 || k.thick < 1 || k.thick > k.p || k.m0 < -20 || k.m0 > 60) return `${what}: wheel pattern out of range`;
  }
  return null;
}
/** Returns an error message, or null when the spec is usable. */
export function validateSpec(x: unknown): string | null {
  const s = x as SpriteSpec;
  if (!s || typeof s !== "object") return "bad sprite";
  let e = checkRows(s.rows, "straight view");
  if (e) return e;
  const W = s.rows[0].length, H = s.rows.length;
  if ((e = checkWheels(s.wheels, "straight view", W, H))) return e;
  if (s.front !== undefined && (e = checkRows(s.front, "front view"))) return e;
  if (s.yaw !== "auto" && s.yaw !== "manual") return "bad pose mode";
  if (!Array.isArray(s.nominal) || s.nominal.length !== 2 || !s.nominal.every((n) => isInt(n) && n >= 4 && n <= 48)) return "on-screen widths: integers 4 to 48";
  if (s.yaw === "auto") {
    if (typeof s.lean !== "number" || !(s.lean >= 0 && s.lean <= 6)) return "lean: 0 to 6";
    if (!Array.isArray(s.flank) || s.flank.length !== 3 || !s.flank.every(isInt) || s.flank[0] < 0 || s.flank[1] < s.flank[0] || s.flank[1] >= H || s.flank[2] < 0 || s.flank[2] > 6) return "flank: [first row, last row, width 0-6]";
    if (W + Math.ceil(s.lean * 2) + s.flank[2] * 2 > 64) return "turning pose too wide";
  } else {
    for (const [i, rows] of [s.yaw1, s.yaw2].entries()) {
      if ((e = checkRows(rows, `turn pose ${i + 1}`, 64))) return e;
      if ((e = checkWheels(s.yawWheels?.[i], `turn pose ${i + 1}`, rows[0].length, rows.length))) return e;
      const a = s.anchors?.[i];
      if (typeof a !== "number" || !(a >= 0 && a <= rows[0].length)) return `turn pose ${i + 1}: centre column outside the sprite`;
    }
  }
  return null;
}

// ---- built-in vehicles (class 1 = rally car, 2 = bike, 3 = kart; class 0, the formula car, lives in the renderer) ----
function painter(W: number, H: number) {
  const g = Array.from({ length: H }, () => Array(W).fill("."));
  const rect = (x: number, y: number, w: number, h: number, ch: string) => {
    for (let j = y; j < y + h; j++) for (let i = x; i < x + w; i++) if (i >= 0 && i < W && j >= 0 && j < H) g[j][i] = ch;
  };
  const sym = (x: number, y: number, w: number, h: number, ch: string) => (rect(x, y, w, h, ch), rect(W - x - w, y, w, h, ch));
  return { rect, sym, rows: () => g.map((r) => r.join("")) };
}

const STOCK_W = 29, STOCK_H = 14;
function stockStraight(): string[] {
  // low, wide rally coupe seen from behind: big wing, short sloping cabin, flared arches, light bar, number roundel, diffuser
  const P = painter(STOCK_W, STOCK_H);
  P.rect(3, 0, 23, 1, "#"); P.sym(3, 0, 1, 3, "#"); // wing and its end plates
  P.rect(9, 1, 11, 1, "#"); // roof
  P.rect(7, 2, 15, 3, "#"); P.rect(9, 2, 11, 2, "o"); P.rect(14, 2, 1, 2, "#"); // rear window and wiper
  P.rect(4, 4, 21, 1, "#"); // boot lip
  P.rect(0, 5, 29, 5, "@"); P.sym(0, 5, 1, 5, "#"); // body, wide flared flanks
  P.sym(2, 5, 6, 2, "+"); P.rect(8, 6, 13, 1, "#"); // tail lights joined by a bar
  P.rect(12, 7, 5, 3, "o"); P.rect(13, 8, 3, 1, "#"); // number roundel
  P.rect(1, 10, 27, 1, "#"); P.rect(3, 11, 23, 1, "#"); // bumper
  P.rect(8, 12, 13, 1, "#"); P.rect(22, 12, 3, 1, "#"); // diffuser and exhaust
  P.sym(0, 8, 5, 6, "#"); // wide tyres
  return P.rows();
}
/** Coupe turning right: the rear face narrows and the right flank shows, its roof sloping down to the bonnet, ending in the front wheel. */
function stockYaw(level: 1 | 2): { rows: string[]; wheels: Wheel[] } {
  const W = 40, H = STOCK_H;
  const g = Array.from({ length: H }, () => Array(W).fill("."));
  const rect = (x: number, y: number, w: number, h: number, ch: string) => {
    for (let j = y; j < y + h; j++) for (let i = x; i < x + w; i++) if (i >= 0 && i < W && j >= 0 && j < H) g[j][i] = ch;
  };
  const rw = level === 2 ? 15 : 21, fl = level === 2 ? 21 : 11;
  rect(0, 0, rw - 1, 1, "#"); rect(0, 0, 1, 3, "#"); // wing
  rect(2, 1, rw - 5, 4, "#"); rect(3, 2, rw - 7, 2, "o"); // cabin and rear window
  rect(0, 5, rw, 5, "@"); rect(0, 5, 1, 5, "#"); // rear face
  rect(1, 5, Math.min(5, rw - 8), 2, "+"); rect(rw - 5, 5, 4, 2, "+"); // tail lights
  if (rw > 18) { rect(rw / 2 - 2, 7, 4, 2, "o"); } // number roundel (hidden in the deep pose)
  rect(0, 10, rw, 1, "#"); rect(2, 11, rw - 4, 1, "#");
  for (let k = 0; k < fl; k++) {
    const x = rw + k, top = 1 + Math.floor((k * 5) / fl); // the roof line falls towards the bonnet
    rect(x, top, 1, 10 - top, "@"); g[top][x] = "#"; g[10][x] = "#";
    if (k > 1 && k < fl * 0.6 && top + 1 <= 4) rect(x, top + 1, 1, Math.min(2, 5 - top), "o"); // side window
    if (k === Math.floor(fl * 0.55)) rect(x, top + 1, 1, 9 - top, "#"); // door line
  }
  rect(rw + fl - 1, 4, 1, 7, "#"); // front edge
  for (let k = 2; k < fl - 2; k++) if (g[6][rw + k] === "@") g[6][rw + k] = "+"; // shoulder highlight
  for (let k = 0; k < fl; k++) if (g[9][rw + k] === "@") g[9][rw + k] = "#"; // side skirt
  const dk = rw + Math.floor(fl * 0.55);
  if (level === 2) { g[7][dk + 2] = "+"; g[8][dk + 2] = "+"; } // door handle
  rect(0, 8, 5, 6, "#"); // rear tyres
  rect(rw - 4, 8, 4, 6, "#");
  const fw = level === 2 ? 5 : 3, fx = rw + fl - fw - 1;
  if (level === 2) { rect(rw + 1, 6, 8, 1, "#"); rect(rw + 2, 7, 6, 7, "#"); } // flared rear arch and the near rear tyre
  rect(fx - 1, 6, fw + 2, 1, "#"); // front arch
  rect(fx, 7, fw, 7, "#"); // front tyre
  const wheels: Wheel[] = [
    { x: 1, mw: 2, y0: 10, y1: 12, m0: 10, p: 3, thick: 1 },
    { x: rw - 3, mw: 2, y0: 10, y1: 12, m0: 10, p: 3, thick: 1 },
    { x: fx + 1, mw: Math.max(1, fw - 2), y0: 9, y1: 12, m0: 9, p: 2, thick: 1 },
    ...(level === 2 ? [{ x: rw + 4, mw: 2, y0: 9, y1: 12, m0: 9, p: 3, thick: 1 }] : []),
  ];
  return { rows: applyRoll(g.map((r) => r.join("")), wheels, 0), wheels };
}
function bikeRows(): string[] {
  const P = painter(15, 17);
  P.rect(5, 0, 5, 1, "#"); P.rect(4, 1, 7, 3, "#"); P.rect(5, 2, 2, 1, "o"); // helmet
  P.rect(2, 4, 11, 1, "#"); // shoulders
  P.rect(3, 5, 9, 4, "@"); P.rect(6, 6, 3, 1, "+"); // back + stripe
  P.sym(1, 5, 2, 5, "#"); // arms
  P.rect(3, 9, 9, 2, "#"); P.sym(1, 9, 2, 3, "#"); // hips and knees
  P.rect(5, 11, 5, 1, "@"); P.rect(6, 11, 3, 1, "+"); // tail cowl + light
  P.rect(5, 12, 5, 5, "#"); // rear tyre
  P.sym(3, 13, 1, 3, "@"); // exhaust pipes
  return P.rows();
}
function kartRows(): string[] {
  const P = painter(19, 11);
  P.rect(7, 0, 5, 3, "#"); P.rect(8, 1, 2, 1, "o"); // helmet
  P.rect(5, 3, 9, 2, "@"); P.rect(5, 3, 9, 1, "#"); // shoulders
  P.rect(6, 5, 7, 3, "#"); P.rect(7, 6, 5, 1, "+"); // seat / engine
  P.sym(0, 4, 4, 7, "#"); // rear tyres
  P.rect(4, 8, 11, 1, "#"); // axle
  P.rect(3, 9, 13, 1, "+"); P.rect(3, 10, 13, 1, "#"); // bumper bar
  return P.rows();
}
function autoSpec(rows: string[], wheels: Wheel[], lean: number, flank: [number, number, number], nominal: [number, number]): SpriteSpec {
  const c = (rows[0].length - 1) / 2;
  const base: SpriteSpec = { rows, wheels, yaw: "auto", lean, flank, yaw1: [], yaw2: [], yawWheels: [wheels, wheels], anchors: [c, c], nominal };
  const y = yawViews(base);
  return { ...base, yaw1: y.rows[0], yaw2: y.rows[1] }; // so that switching to hand-drawn poses starts from the generated ones
}
const st1 = stockYaw(1), st2 = stockYaw(2);
/** Index = vehicle category; [0] (formula) is drawn by the renderer itself and cannot be edited. */
export const DEFAULT_SPECS: (SpriteSpec | null)[] = [
  null,
  {
    rows: stockStraight(),
    wheels: [{ x: 1, mw: 2, y0: 10, y1: 12, m0: 10, p: 3, thick: 1 }, { x: 26, mw: 2, y0: 10, y1: 12, m0: 10, p: 3, thick: 1 }],
    yaw: "manual", lean: 1.2, flank: [5, 11, 3],
    yaw1: st1.rows, yaw2: st2.rows, yawWheels: [st1.wheels, st2.wheels], anchors: [10, 7], nominal: [29, 27],
  },
  autoSpec(bikeRows(), [{ x: 6, mw: 3, y0: 12, y1: 16, m0: 12, p: 3, thick: 1 }], 2.4, [0, 0, 0], [15, 14]),
  autoSpec(kartRows(), [{ x: 1, mw: 2, y0: 5, y1: 9, m0: 5, p: 3, thick: 1 }, { x: 16, mw: 2, y0: 5, y1: 9, m0: 5, p: 3, thick: 1 }], 1.6, [4, 8, 1], [19, 18]),
];
